import { AccordionSummary, accordionSummaryClasses, Theme, Typography, useTheme } from "@mui/material";
import { Stack } from "@mui/system";
import { Dispatch, SetStateAction } from "react";

import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import AccentDanger from "../../../icons/AccentDanger.svg?react";
import AccentInfo from "../../../icons/AccentInfo.svg?react";
import AccentSuccess from "../../../icons/AccentSuccess.svg?react";
import Issues from "../../../icons/Issues.svg?react";
import { toTitleCase } from "../../../lib/Util";
import { GroupingAccordion } from "./GroupingAccordion";
import { NoItems } from "./Rows";
import { InfoStatus } from "./types";

type FilterStatus = "Error" | "Issue" | "Notice" | "Working";

const getColorByStatus = (status: FilterStatus, theme: Theme) => {
  switch (status) {
    case "Error":
      return theme.palette.accentDanger20.main;
    case "Issue":
      return theme.palette.accentWarning20.main;
    case "Notice":
      return theme.palette.accentInfo20.main;
    case "Working":
      return theme.palette.accentSuccess20.main;
  }
};

const getIconByStatus = (status: FilterStatus, theme: Theme) => {
  switch (status) {
    case "Error":
      return (
        <AccentDanger
          width="18px"
          height="18px"
          fill={theme.palette.accentDanger30.main}
          color={theme.palette.text10.main}
        />
      );
    case "Issue":
      return (
        <Issues
          width="18px"
          height="18px"
          fill={theme.palette.accentWarning30.main}
          color={theme.palette.text10.main}
        />
      );
    case "Notice":
      return (
        <AccentInfo
          width="18px"
          height="18px"
          fill={theme.palette.accentInfo30.main}
          color={theme.palette.text10.main}
        />
      );
    case "Working":
      return (
        <AccentSuccess
          width="18px"
          height="18px"
          fill={theme.palette.accentSuccess30.main}
          color={theme.palette.text10.main}
        />
      );
  }
};

const AccordionSummaryStatus = ({ name, status }: { name: string; status: FilterStatus }) => {
  const theme = useTheme();
  return (
    <AccordionSummary
      expandIcon={<CaretDownIcon size="24px" color={theme.palette.text50.main} />}
      aria-controls="panel1-content"
      id="panel1-header"
      sx={{
        backgroundColor: getColorByStatus(status, theme),
        padding: "16px",
        height: "56px",
        borderRadius: "8px",
        [`&.${accordionSummaryClasses.expanded}`]: {
          borderRadius: "8px 8px 0 0",
        },
      }}
    >
      <Stack direction="row" sx={{ width: "100%", gap: "8px", alignItems: "center", ml: "40px" }}>
        <>{getIconByStatus(status, theme)}</>
        <Typography variant="heading.seven" sx={{ color: "text50.main" }}>
          {name}
        </Typography>
      </Stack>
    </AccordionSummary>
  );
};

type StatusesProps = {
  infoStatus: InfoStatus;
  sparklineFirstDate: string;
  expandedItems: string[];
  setExpandedItems: Dispatch<SetStateAction<string[]>>;
  refreshData: () => Promise<void>;
  refreshing: boolean;
};

const Statuses = ({
  infoStatus,
  sparklineFirstDate,
  expandedItems,
  setExpandedItems,
  refreshData,
  refreshing,
}: StatusesProps) => {
  if (Object.keys(infoStatus).length === 0) {
    return <NoItems />;
  }
  return (
    <Stack sx={{ gap: "16px" }}>
      {(Object.keys(infoStatus) as (keyof InfoStatus)[]).map((key, index) => {
        const sortedDownloads = infoStatus[key]?.downloads?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        const sortedUploads = infoStatus[key]?.uploads?.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
        return (
          <GroupingAccordion
            refreshData={refreshData}
            index={index}
            key={key}
            downloads={sortedDownloads ?? []}
            uploads={sortedUploads ?? []}
            sparklineFirstDate={sparklineFirstDate}
            expanded={expandedItems.includes(key)}
            setExpanded={(expanded: boolean) => {
              setExpandedItems((prevState) =>
                expanded ? [...prevState, key] : prevState.filter((project) => project !== key)
              );
            }}
            refreshing={refreshing}
          >
            <AccordionSummaryStatus name={toTitleCase(key)} status={key as FilterStatus} />
          </GroupingAccordion>
        );
      })}
    </Stack>
  );
};

export { Statuses };
